/**
 * Consumer hook: overlays in-flight optimistic connection operations onto query data.
 *
 * A component reads its own query data (`base`, pristine server truth) and overlays every
 * in-flight operation targeting the same owner/connection, gathered from TanStack Query's
 * mutation cache via `useMutationState` — no event bus, no broadcast; consumers of the same
 * connection independently select the same pending operations by `mutationKey`.
 *
 * Settle via `invalidateQueries` + refetch (or a live-update channel) rather than
 * `setQueryData(responseBody)`, so an out-of-order response can't fold a stale value in.
 */
import {useMutationState} from '@github-ui/react-query'
import {useMemo} from 'react'

import {
  type ConnectionOperation,
  type EntityOperation,
  type PendingConnectionOperation,
  DOCUMENT_FIELDS_PATH,
} from './connection-operations'

/** The shared root segment of every connection mutation key; `useOptimisticData`
 *  subscribes to this broad prefix (see the `useMutationState` call). */
export const CONNECTION_MUTATION_KEY_ROOT = 'connection'

/**
 * The stable, query-agnostic mutation key for a connection. Producers and consumers must
 * agree on this so pending operations can be gathered. The path stays a nested array (not
 * joined) so `['a','b']` and `['a.b']` produce distinct keys; TanStack hashes it structurally.
 */
export function connectionMutationKey(ownerId: string, connectionPath: string[]): unknown[] {
  return [CONNECTION_MUTATION_KEY_ROOT, ownerId, connectionPath]
}

/**
 * The owner an **entity** operation is keyed under. A `setFields` names its own target
 * (`operation.id`) and `useOptimisticData` matches it against the identities present in a
 * document, so unlike a structural operation it needs no owner to be routed correctly.
 *
 * That is what lets one key serve field edits for *every* entity a document renders — the issue,
 * its parent, its sub-issue rows, its milestone. Keying by owner instead would mean enumerating
 * those ids up front and silently missing any the enumeration didn't anticipate.
 */
export const ENTITY_FIELDS_OWNER = '__entityFields'

/** The stable mutation key every entity field operation is stored under, whether it was
 *  published natively by `useEntityMutation` or mirrored in from Relay by the bridge. */
export function entityFieldsMutationKey(): unknown[] {
  return connectionMutationKey(ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
}

/** The `useMutation` variables shape a connection mutation stores and this hook reads back. */
export type ConnectionMutationVariables = {operation: ConnectionOperation; operationId?: string; mutationId?: string}

/** A pending operation read from the mutation cache, carrying the owner and path from its
 *  own key so a stale `useMutationState` snapshot can be filtered out synchronously. A
 *  superset of `PendingConnectionOperation`, so it feeds `applyOperations` directly. */
type PendingEntry = PendingConnectionOperation & {connectionPath: string[]}

export type ConnectionMutationTarget = {ownerId: string; connectionPath: string[]}
export type SetFieldsMapper = (fields: Readonly<Record<string, unknown>>) => Record<string, unknown> | undefined
export type EntityFieldMapper = (field: string) => string | undefined

export function connectionTargetKey(ownerId: string, connectionPath: string[]): string {
  return JSON.stringify([ownerId, connectionPath])
}

/**
 * Every pending operation in the mutation cache, in dispatch order. Shared by the consumer
 * hooks so the subscription's subtleties live in one place.
 */
export function usePendingOperationEntries(): Array<PendingEntry | undefined> {
  const entries = useMutationState<PendingEntry | undefined>({
    // Subscribe to the broad `['connection']` prefix, not a consumer's exact key:
    // `useMutationState` only recomputes on a mutation-cache notification, not when its
    // filter options change, so a key-scoped subscription would momentarily drop the newly
    // selected connection's already-pending operations when a mounted consumer switches
    // connections. Each consumer narrows synchronously in its own memo.
    filters: {mutationKey: [CONNECTION_MUTATION_KEY_ROOT], status: 'pending'},
    // Carry the owner and path from the mutation's own key, in insertion (dispatch) order —
    // the last-write-wins tiebreaker `applyOperations` needs.
    select: mutation => {
      const variables = mutation.state.variables as ConnectionMutationVariables | undefined
      const operation = variables?.operation
      const key = mutation.options.mutationKey
      if (!operation || !Array.isArray(key) || key[0] !== CONNECTION_MUTATION_KEY_ROOT) return undefined
      return {
        ownerId: String(key[1]),
        connectionPath: Array.isArray(key[2]) ? (key[2] as string[]) : [],
        operation,
        operationId: variables.operationId ?? variables.mutationId,
      }
    },
  })

  return useMemo(() => {
    const seenOperationIds = new Set<string>()
    return entries.filter(entry => {
      if (!entry?.operationId) return true
      if (seenOperationIds.has(entry.operationId)) return false
      seenOperationIds.add(entry.operationId)
      return true
    })
  }, [entries])
}

/** Applies a consumer's field-name projection, dropping the operation when it maps to nothing. */
export function projectEntityOperation(
  operation: EntityOperation,
  mapSetFields: SetFieldsMapper | undefined,
  mapEntityField: EntityFieldMapper | undefined,
): EntityOperation | undefined {
  if (operation.type === 'setFields' && mapSetFields) {
    const fields = mapSetFields(operation.fields)
    if (!fields) return undefined
    return {...operation, fields}
  }
  if (operation.type === 'deltaEntityField' && mapEntityField) {
    const field = mapEntityField(operation.field)
    return field ? {...operation, field} : undefined
  }
  return operation
}
